import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { cx } from '../utils/cx.js';
import styles from './SegmentedControl.module.css';
import { GhostWeightLabel } from './GhostWeightLabel.js';
import { Pressable } from './Pressable.js';
import { ProximityHighlight } from './ProximityHighlight.js';
import { useProximityHover } from '../hooks/useProximityHover.js';

export type SegmentedOption<T extends string = string> = { value: T; label: ReactNode };

export type SegmentedControlProps<T extends string = string> = {
  value: T;
  onChange: (v: T) => void;
  options: SegmentedOption<T>[];
  'aria-label'?: string;
  /** `tabs` — display type + meta/brunswick ink (product detail, taxonomy, etc.). */
  variant?: 'default' | 'tabs';
  /**
   * When true, hide vertical separators adjacent to the active option.
   * Default false — hiding seps makes group border look broken on hover/focus.
   */
  hideNearActiveSeparators?: boolean;
};

type ThumbRect = { left: number; width: number; ready: boolean };

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  'aria-label': ariaLabel,
  variant = 'default',
  hideNearActiveSeparators = false,
}: SegmentedControlProps<T>) {
  const groupRef = useRef<HTMLDivElement>(null);
  const btnRefs = useRef(new Map<string, HTMLButtonElement>());
  const [thumb, setThumb] = useState<ThumbRect>({ left: 0, width: 0, ready: false });
  const optionsKey = useMemo(() => options.map((o) => o.value).join('\0'), [options]);

  useLayoutEffect(() => {
    const group = groupRef.current;
    const btn = btnRefs.current.get(value);
    if (!group || !btn) return;

    const update = () => {
      const g = group.getBoundingClientRect();
      const b = btn.getBoundingClientRect();
      // Round — subpixel jitter from RO must not thrash setState.
      const left = Math.round(b.left - g.left);
      const width = Math.round(b.width);
      setThumb((prev) =>
        prev.ready && prev.left === left && prev.width === width
          ? prev
          : { left, width, ready: true },
      );
    };

    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(group);
    window.addEventListener('resize', update);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [value, optionsKey]);

  const proximity = useProximityHover<HTMLDivElement>({ axis: 'x' });
  const setGroupRef = (node: HTMLDivElement | null) => {
    groupRef.current = node;
    (proximity.containerRef as { current: HTMLDivElement | null }).current = node;
  };

  return (
    <div
      ref={setGroupRef}
      className={cx(styles.group, variant === 'tabs' && styles.groupTabs)}
      role="group"
      aria-label={ariaLabel}
      {...proximity.containerProps}
    >
      <ProximityHighlight style={proximity.highlightStyle} active={proximity.active} />
      <span
        className={cx(styles.thumb, variant === 'tabs' && styles.thumbTabs, thumb.ready && styles.thumbReady)}
        style={{
          transform: `translateX(${thumb.left}px)`,
          width: thumb.width,
        }}
        aria-hidden
      />
      {options.map((o, i) => (
        <span key={o.value} style={{ display: 'contents' }}>
          {i > 0 ? (
            <span
              className={cx(
                styles.sep,
                hideNearActiveSeparators &&
                  (options[i - 1]?.value === value || o.value === value) &&
                  styles.sepNearActive,
              )}
              aria-hidden
            />
          ) : null}
          <Pressable
            type="button"
            ref={(el) => {
              if (el) btnRefs.current.set(o.value, el);
              else btnRefs.current.delete(o.value);
            }}
            className={cx(styles.btn, value === o.value && styles.active)}
            data-wp-proximity-item=""
            onClick={() => onChange(o.value)}
            aria-pressed={value === o.value}
          >
            {typeof o.label === 'string' || typeof o.label === 'number' ? (
              <GhostWeightLabel active={value === o.value}>{o.label}</GhostWeightLabel>
            ) : (
              o.label
            )}
          </Pressable>
        </span>
      ))}
    </div>
  );
}
